import React, { useEffect, useRef, useState, useCallback } from "react";
import * as d3 from "d3";

function NettedGraphVisualization() {
  const containerRef = useRef();
  const svgRef = useRef();
  const [error, setError] = useState(null);
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });

  // Function to update dimensions based on container size
  const updateDimensions = () => {
    if (containerRef.current) {
      const { width, height } = containerRef.current.getBoundingClientRect();
      setDimensions({ width, height });
    }
  };

  // Initialize dimensions and add resize listener
  useEffect(() => {
    updateDimensions();
    window.addEventListener("resize", updateDimensions);

    return () => {
      window.removeEventListener("resize", updateDimensions);
    };
  }, []);

  const createVisualization = useCallback(
    (data) => {
      try {
        const nodes = new Set();
        const links = [];

        Object.keys(data).forEach((source) => {
          nodes.add(source);
          Object.keys(data[source]).forEach((target) => {
            nodes.add(target);
          });
        });

        const nodesArray = Array.from(nodes).map((id) => ({ id }));

        Object.entries(data).forEach(([source, targets]) => {
          Object.entries(targets).forEach(([target, metadata]) => {
            links.push({
              source,
              target,
              units: metadata.units,
              secondLegTotal: metadata.second_leg_total,
            });
          });
        });

        // Use the dynamic dimensions
        const width = dimensions.width;
        const height = dimensions.height;

        // Scale node size based on the container size
        const nodeRadius = Math.min(width, height) * 0.025; // Adaptive node size
        const fontSize = Math.min(width, height) * 0.02; // Adaptive font size

        // Enhanced force simulation with adaptive parameters
        const simulation = d3
          .forceSimulation(nodesArray)
          .force(
            "link",
            d3
              .forceLink(links)
              .id((d) => d.id)
              .distance(Math.min(width, height) * 0.25)
          )
          .force(
            "charge",
            d3.forceManyBody().strength(-Math.min(width, height) * 1.5)
          )
          .force("center", d3.forceCenter(width / 2, height / 2))
          .force("x", d3.forceX(width / 2).strength(0.1))
          .force("y", d3.forceY(height / 2).strength(0.1))
          .force("collision", d3.forceCollide().radius(nodeRadius * 1.5));

        const svg = d3
          .select(svgRef.current)
          .attr("width", width)
          .attr("height", height)
          .attr("viewBox", [0, 0, width, height])
          .attr("style", "width: 100%; height: 100%;");

        svg.selectAll("*").remove();

        // Adaptive arrow marker
        svg
          .append("defs")
          .append("marker")
          .attr("id", "arrowhead")
          .attr("viewBox", "-0 -5 10 10")
          .attr("refX", nodeRadius + 3) // Adjusted to place arrow at node edge
          .attr("refY", 0)
          .attr("orient", "auto")
          .attr("markerWidth", fontSize * 0.5)
          .attr("markerHeight", fontSize * 0.5)
          .append("svg:path")
          .attr("d", "M 0,-5 L 10 ,0 L 0,5")
          .attr("fill", "#666")
          .style("stroke", "none");

        // Straight edges with adaptive width
        const link = svg
          .append("g")
          .selectAll("line")
          .data(links)
          .join("line")
          .attr("class", "link")
          .attr("marker-end", "url(#arrowhead)")
          .style("stroke", "#666")
          .style("stroke-width", (d) =>
            Math.max(1, Math.sqrt(d.units) * (width / 800))
          );

        // Edge labels with adaptive size
        const linkLabels = svg
          .append("g")
          .selectAll("text")
          .data(links)
          .join("text")
          .attr("class", "edge-label")
          .style("font-size", `${fontSize}px`)
          .style("text-anchor", "middle")
          .text((d) => `${d.units} T | $${d.secondLegTotal}`);

        // Nodes with adaptive size
        const node = svg
          .append("g")
          .selectAll("circle")
          .data(nodesArray)
          .join("circle")
          .attr("class", "node")
          .attr("r", nodeRadius)
          .style("fill", "#69b3a2")
          .style("stroke", "#fff")
          .style("stroke-width", Math.max(1, width / 600))
          .call(drag(simulation));

        // Node labels with adaptive size
        const labels = svg
          .append("g")
          .selectAll("text")
          .data(nodesArray)
          .join("text")
          .text((d) => d.id)
          .attr("font-size", `${fontSize}px`)
          .attr("font-weight", "bold")
          .attr("text-anchor", "middle")
          .attr("dy", ".35em")
          .style("fill", "#fff");

        // Hover tooltips
        node.append("title").text((d) => d.id);

        link
          .append("title")
          .text(
            (d) =>
              `${d.source.id} → ${d.target.id}\nUnits: ${d.units}\nTotal: $${d.secondLegTotal}`
          );

        // Update positions on each tick
        simulation.on("tick", () => {
          // Update link positions
          link
            .attr("x1", (d) => d.source.x)
            .attr("y1", (d) => d.source.y)
            .attr("x2", (d) => d.target.x)
            .attr("y2", (d) => d.target.y);

          // Update edge label positions
          linkLabels
            .attr("x", (d) => (d.source.x + d.target.x) / 2)
            .attr("y", (d) => (d.source.y + d.target.y) / 2 - nodeRadius / 2);

          // Update node positions with boundary constraints
          node
            .attr(
              "cx",
              (d) =>
                (d.x = Math.max(nodeRadius, Math.min(width - nodeRadius, d.x)))
            )
            .attr(
              "cy",
              (d) =>
                (d.y = Math.max(nodeRadius, Math.min(height - nodeRadius, d.y)))
            );

          // Update node label positions
          labels.attr("x", (d) => d.x).attr("y", (d) => d.y);
        });
      } catch (error) {
        console.error("Error creating visualization:", error);
        setError("Failed to create visualization: " + error.message);
      }
    },
    [dimensions.height, dimensions.width]
  );
  // Fetch data and create visualization when dimensions change
  useEffect(() => {
    if (dimensions.width === 0 || dimensions.height === 0) return;

    const fetchGraphData = async () => {
      try {
        const response = await fetch("https://api.repo.tradingmechanism.com/get_netted");
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || "Failed to fetch graph data");
        }

        const data = await response.json();
        console.log("Received data:", data);

        if (!data || typeof data !== "object") {
          throw new Error("Invalid data format received");
        }

        createVisualization(data);
      } catch (error) {
        console.error("Error fetching graph data:", error);
        setError(error.message);
      }
    };

    fetchGraphData();
  }, [dimensions, createVisualization]);
  const drag = (simulation) => {
    function dragstarted(event) {
      if (!event.active) simulation.alphaTarget(0.3).restart();
      event.subject.fx = event.subject.x;
      event.subject.fy = event.subject.y;
    }

    function dragged(event) {
      event.subject.fx = event.x;
      event.subject.fy = event.y;
    }

    function dragended(event) {
      if (!event.active) simulation.alphaTarget(0);
      event.subject.fx = null;
      event.subject.fy = null;
    }

    return d3
      .drag()
      .on("start", dragstarted)
      .on("drag", dragged)
      .on("end", dragended);
  };

  return (
    <div
      ref={containerRef}
      className="graph-container"
      style={{
        width: "100%",
        height: "100%",
        minHeight: "600px",
        position: "relative",
      }}
    >
      {error && (
        <div
          className="error-message"
          style={{ color: "red", padding: "1rem" }}
        >
          {error}
        </div>
      )}
      <svg ref={svgRef} style={{ width: "100%", height: "100%" }}></svg>
    </div>
  );
}

export default NettedGraphVisualization;
